/**
 * IndexedDB schema, migration ladder and connection handling (plan D2).
 *
 * Everything the editor persists lives in one database with an explicit version. Schema changes
 * NEVER mutate `applyMigrations`' existing steps — they add a new step and bump `DB_VERSION`, so
 * a user opening an old database is walked forward one version at a time.
 */

import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';
import {
  AppError,
  isAppError,
  type CanonicalAnimationClip,
  type MappingPreset,
  type ProjectRecord,
} from '../shared';

/**
 * Frozen at the pre-rename spelling. The product is called Motion Kit, but this string names an
 * IndexedDB database that already exists in users' browsers — changing it would hide every stored
 * project behind a new, empty database. Same reasoning as `GLB_METADATA_KEY` and `ClipboardPayload`.
 */
export const DB_NAME = 'animstatex';
export const DB_VERSION = 1;

/** Key under which the current schema version is recorded in the `meta` store after every open. */
export const META_SCHEMA_VERSION_KEY = 'schemaVersion';
/** Key under which the wall-clock time of the last successful open is recorded. */
export const META_LAST_OPENED_KEY = 'lastOpenedAt';

/** A GLB (or any other binary payload) kept out of the project record itself. */
export interface BlobRecord {
  id: string;
  blob: Blob;
  mime: string;
  size: number;
  createdAt: number;
}

export interface IntchMotionKitDB extends DBSchema {
  projects: {
    key: string;
    value: ProjectRecord;
    indexes: { updatedAt: number };
  };
  blobs: {
    key: string;
    value: BlobRecord;
  };
  /** The global animation library — clips are never copied into project records. */
  clips: {
    key: string;
    value: CanonicalAnimationClip;
    indexes: { name: string; updatedAt: number };
  };
  mappingPresets: {
    key: string;
    value: MappingPreset;
    indexes: { name: string };
  };
  /** Out-of-line string keys, arbitrary JSON values. */
  settings: {
    key: string;
    value: unknown;
  };
  /** Out-of-line string keys; schema bookkeeping. */
  meta: {
    key: string;
    value: unknown;
  };
}

export type IntchMotionKitDatabase = IDBPDatabase<IntchMotionKitDB>;

// ---------------------------------------------------------------------------
// Migration ladder
// ---------------------------------------------------------------------------

/**
 * The slice of `IDBDatabase` a migration step is allowed to use. Declared structurally so tests
 * can drive the ladder with a recording double instead of a real connection.
 */
export interface UpgradeStoreLike {
  createIndex(name: string, keyPath: string, options?: IDBIndexParameters): unknown;
}

export interface UpgradeDatabaseLike {
  objectStoreNames: { contains(name: string): boolean };
  createObjectStore(name: string, options?: IDBObjectStoreParameters): UpgradeStoreLike;
}

function createSchemaV1(db: UpgradeDatabaseLike): void {
  const projects = db.createObjectStore('projects', { keyPath: 'id' });
  projects.createIndex('updatedAt', 'updatedAt');

  db.createObjectStore('blobs', { keyPath: 'id' });

  const clips = db.createObjectStore('clips', { keyPath: 'id' });
  clips.createIndex('name', 'name');
  clips.createIndex('updatedAt', 'updatedAt');

  const presets = db.createObjectStore('mappingPresets', { keyPath: 'id' });
  presets.createIndex('name', 'name');

  db.createObjectStore('settings');
  db.createObjectStore('meta');
}

/**
 * One rung of the ladder: bring a database at version `step - 1` to version `step`.
 *
 * Written as a switch so adding version 2 is a one-line `case 2:`. (The plan asked for classic
 * `switch` fall-through; `noFallthroughCasesInSwitch` in tsconfig forbids it, so the caller loops
 * over the steps instead — same semantics, one step per version, none skipped.)
 */
function applyMigrationStep(db: UpgradeDatabaseLike, step: number): void {
  switch (step) {
    case 1:
      createSchemaV1(db);
      break;
    default:
      throw new AppError(
        'DB_MIGRATION_MISSING',
        `This build has no migration to local database version ${step}.`,
        { step, dbVersion: DB_VERSION },
      );
  }
}

/**
 * Walk a database from `oldVersion` to `newVersion`, one version at a time. Exported so the
 * upgrade path is testable without provoking a real version bump.
 */
export function applyMigrations(
  db: UpgradeDatabaseLike,
  oldVersion: number,
  newVersion: number = DB_VERSION,
): number[] {
  const applied: number[] = [];
  for (let step = oldVersion + 1; step <= newVersion; step++) {
    applyMigrationStep(db, step);
    applied.push(step);
  }
  return applied;
}

// ---------------------------------------------------------------------------
// "Another tab is holding the database"
// ---------------------------------------------------------------------------

/**
 * IndexedDB will not upgrade or delete a database while another connection has the old version
 * open. That is not an error the app can retry its way out of — only closing the other tab helps
 * — and a `console.error` leaves the user watching an operation that never finishes (audit P1).
 * So the state is published, `main.ts` renders it, and it clears itself the moment the operation
 * gets through.
 */
export type DatabaseBlockedListener = (blocked: boolean) => void;

const blockedListeners = new Set<DatabaseBlockedListener>();
let databaseBlocked = false;

/** Subscribe to the "blocked by another tab" state. Fires immediately if already blocked. */
export function onDatabaseBlocked(listener: DatabaseBlockedListener): () => void {
  blockedListeners.add(listener);
  if (databaseBlocked) listener(true);
  return () => {
    blockedListeners.delete(listener);
  };
}

/** True while an upgrade or delete is waiting for another tab to let go. */
export function isDatabaseBlocked(): boolean {
  return databaseBlocked;
}

function setDatabaseBlocked(blocked: boolean): void {
  if (databaseBlocked === blocked) return;
  databaseBlocked = blocked;
  for (const listener of [...blockedListeners]) {
    try {
      listener(blocked);
    } catch (error) {
      console.error('[intch-motion-kit] a database-blocked listener threw', error);
    }
  }
}

// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------

let dbPromise: Promise<IntchMotionKitDatabase> | null = null;

async function connect(): Promise<IntchMotionKitDatabase> {
  let db: IntchMotionKitDatabase;
  try {
    db = await openDB<IntchMotionKitDB>(DB_NAME, DB_VERSION, {
      upgrade(database, oldVersion, newVersion) {
        applyMigrations(
          database as unknown as UpgradeDatabaseLike,
          oldVersion,
          newVersion ?? DB_VERSION,
        );
      },
      blocked(currentVersion, blockedVersion) {
        console.error(
          `[intch-motion-kit] database upgrade blocked (open elsewhere at v${currentVersion}, ` +
            `wanted v${blockedVersion}).`,
        );
        setDatabaseBlocked(true);
      },
      blocking() {
        // Another tab wants to upgrade: let go of our connection instead of blocking it.
        void closeDatabase();
      },
      terminated() {
        console.error('[intch-motion-kit] database connection was terminated by the browser.');
        dbPromise = null;
      },
    });
  } catch (e) {
    if (isAppError(e)) throw e;
    throw new AppError(
      'DB_OPEN_FAILED',
      'Could not open the local database. Private browsing or a blocked upgrade can cause this.',
      e,
    );
  }

  // We are through: whatever tab was in the way has let go.
  setDatabaseBlocked(false);

  try {
    const tx = db.transaction('meta', 'readwrite');
    await Promise.all([
      tx.store.put(DB_VERSION, META_SCHEMA_VERSION_KEY),
      tx.store.put(Date.now(), META_LAST_OPENED_KEY),
      tx.done,
    ]);
  } catch (e) {
    db.close();
    throw new AppError('DB_TX_FAILED', 'Could not record the local database schema version.', e);
  }

  return db;
}

/** Open (or reuse) the singleton connection. A failed open is not cached. */
export function openDatabase(): Promise<IntchMotionKitDatabase> {
  if (!dbPromise) {
    const pending = connect().catch((e: unknown) => {
      if (dbPromise === pending) dbPromise = null;
      throw e;
    });
    dbPromise = pending;
  }
  return dbPromise;
}

/** Close the singleton connection (tests, "reset local data", tab handover). */
export async function closeDatabase(): Promise<void> {
  const pending = dbPromise;
  dbPromise = null;
  if (!pending) return;
  try {
    const db = await pending;
    db.close();
  } catch {
    // The open failed; there is nothing to close.
  }
}

/** Drop the whole local datastore. Used by "reset local data" and by tests between cases. */
export async function deleteDatabase(): Promise<void> {
  await closeDatabase();
  try {
    await deleteDB(DB_NAME, {
      blocked() {
        console.error('[intch-motion-kit] delete of the local database is blocked by another tab.');
        setDatabaseBlocked(true);
      },
    });
  } catch (e) {
    throw new AppError('DB_DELETE_FAILED', 'Could not delete the local database.', e);
  }
  setDatabaseBlocked(false);
}

/** The schema version recorded in `meta` (undefined only if the store was never written). */
export async function readSchemaVersion(): Promise<number | undefined> {
  const db = await openDatabase();
  const value = await db.get('meta', META_SCHEMA_VERSION_KEY);
  return typeof value === 'number' ? value : undefined;
}

// ---------------------------------------------------------------------------
// Error helpers shared by the repositories
// ---------------------------------------------------------------------------

/** Normalize any failure raised inside a transaction into a user-facing `AppError`. */
export function asDbError(e: unknown): AppError {
  if (isAppError(e)) return e;
  return new AppError('DB_TX_FAILED', 'A local database operation failed.', e);
}

/** Run `fn` against the open database, wrapping unexpected failures as `DB_TX_FAILED`. */
export async function withDb<T>(fn: (db: IntchMotionKitDatabase) => Promise<T>): Promise<T> {
  const db = await openDatabase();
  try {
    return await fn(db);
  } catch (e) {
    const error = asDbError(e);
    console.error('[intch-motion-kit] database operation failed', error.details ?? error);
    throw error;
  }
}
